import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuLayoutTemplate } from 'react-icons/lu';

export interface EmailColorsMenuLabels {
    trigger: string;
    menu: string;
    canvas: string;
    header: string;
    card: string;
}

export interface EmailColorsMenuProps {
    buttonClassName: string;
    labels: EmailColorsMenuLabels;
    canvasColor: string;
    headerColor: string;
    cardColor: string;
    onCanvasChange: (_color: string) => void;
    onHeaderChange: (_color: string) => void;
    onCardChange: (_color: string) => void;
}

export const EmailColorsMenu = (props: EmailColorsMenuProps) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState<CSSProperties>();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;

        const updatePosition = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            setMenuStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                zIndex: 1050
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return (
        <div className='relative'>
            <button
                id='email-colors-button'
                ref={triggerRef}
                type='button'
                aria-label={props.labels.trigger}
                title={props.labels.trigger}
                aria-expanded={open}
                aria-haspopup='dialog'
                onClick={() => setOpen((current) => !current)}
                className={props.buttonClassName}>
                <LuLayoutTemplate className='size-3.5' />
            </button>
            {open
                ? createPortal(
                      <div
                          ref={menuRef}
                          role='dialog'
                          aria-label={props.labels.menu}
                          style={menuStyle}
                          className='bg-background-surface outline-border-default flex min-w-52 flex-col gap-2 rounded-md p-3 outline-1 outline-offset-[-1px]'>
                          <EmailColorRow
                              inputId='email-canvas-color-input'
                              label={props.labels.canvas}
                              color={props.canvasColor}
                              onChange={props.onCanvasChange}
                          />
                          <EmailColorRow
                              inputId='email-header-color-input'
                              label={props.labels.header}
                              color={props.headerColor}
                              onChange={props.onHeaderChange}
                          />
                          <EmailColorRow
                              inputId='email-card-color-input'
                              label={props.labels.card}
                              color={props.cardColor}
                              onChange={props.onCardChange}
                          />
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
};

const EmailColorRow = (props: { inputId: string; label: string; color: string; onChange: (_color: string) => void }) => (
    <label className='text-foreground-default flex items-center justify-between gap-3 text-sm font-medium'>
        <span>{props.label}</span>
        <span className='outline-border-default relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md outline-1 outline-offset-[-1px]'>
            <span
                className='absolute inset-1 rounded-sm'
                style={{ backgroundColor: props.color }}
            />
            <input
                id={props.inputId}
                type='color'
                aria-label={props.label}
                value={props.color}
                onChange={(event) => props.onChange(event.target.value)}
                className='absolute inset-0 cursor-pointer opacity-0'
            />
        </span>
    </label>
);
